import React, { useState, useEffect, useCallback, useMemo } from 'react';
import * as XLSX from 'xlsx-js-style';
import client from '../../services/graphqlClient';
import { useNotification } from '../../context/NotificationContext';
import { useAuth } from '../../context/AuthContext';
import { semaforoCronograma } from '../../utils/semaforoCronograma';
import './VistaFinancieraGerencia.css';

const formatCOP = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '-';
  return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(val);
};

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

// Todos los estados financieros con su color de marca. Orden = pipeline natural.
const ESTADOS = {
  vendido:                { label: 'Vendido',         color: '#06b6d4' },
  contratado_no_iniciado: { label: 'Contratado',      color: '#6366f1' },
  en_ejecucion:           { label: 'En Ejecución',    color: '#22c55e' },
  entregado_con_detalles: { label: 'Con Detalles',    color: '#3b82f6' },
  cobrado_liquidado:      { label: 'En Cobro',        color: '#f59e0b' },
  entregado_liquidado:    { label: 'Liquidado',       color: '#8b5cf6' },
};

const QUERY = `query($fc: String) {
  proyectosFinancieros(fecha_corte: $fc) {
    origen fecha_corte caja_bancos nota
    proyectos {
      id proyecto_nombre nombre_cliente cedula telefono correo apartamento area_m2
      estado estado_cartera fecha_cotizacion fecha_entrega_estimada fecha_inicio_cronograma
      valor_final valor_ejecutado valor_ejecutado_facturas facturas_montadas facturas_pendiente gasto_manual caja_menor salidas_bodega
      valor_pagado_cliente pendiente_cliente utilidad_actual
      porcentaje_ejecutado porcentaje_cobrado
    }
    totales { valor_final valor_ejecutado valor_pagado_cliente pendiente_cliente }
  }
}`;

// Fecha de hoy en Colombia (AAAA-MM-DD) para el selector de fecha de corte.
const hoyBogota = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
const fechaLargaCO = (iso) => iso ? new Date(iso + 'T12:00:00Z').toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'America/Bogota' }) : '';
const ORIGEN_LABEL = { actual: 'Valores actuales', foto: 'Foto guardada ese día', reconstruido: 'Reconstruido (aproximado)' };

function Badge({ estado }) {
  const cfg = ESTADOS[estado] || { label: estado || '-', color: '#94a3b8' };
  return <span className="vfg-badge" style={{ background: cfg.color }}>{cfg.label}</span>;
}

function BarVal({ valor, pct }) {
  const w = clamp(pct || 0, 0, 100);
  const color = pct > 100 ? '#ef4444' : pct > 80 ? '#f59e0b' : '#3b82f6';
  return (
    <div className="vfg-barval">
      <span>{formatCOP(valor)}</span>
      <div className="vfg-bar-track"><div className="vfg-bar-fill" style={{ width: `${w}%`, background: color }} /></div>
      <span className="vfg-pct">{(pct || 0).toFixed(1)}%</span>
    </div>
  );
}

// Construye el archivo Excel estilizado (3 hojas) para presentación a gerencia.
// Sheet 1: Resumen ejecutivo con KPIs + breakdown por estado.
// Sheet 2: Detalle completo de proyectos.
// Sheet 3: Análisis por estado.
function generarExcel(proyectos, totales, corte = {}) {
  const wb = XLSX.utils.book_new();
  // Con fecha de corte, el informe dice de qué día es y de dónde salió (foto o reconstrucción).
  const fechaHoy = corte.fecha_corte ? fechaLargaCO(corte.fecha_corte) : new Date().toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  const origenTxt = corte.origen && corte.origen !== 'actual' ? ` · ${ORIGEN_LABEL[corte.origen] || corte.origen}` : '';

  // Estilos reutilizables
  const styleTitulo = {
    font: { name: 'Calibri', sz: 18, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '1C2837' } },
    alignment: { vertical: 'center', horizontal: 'center' },
    border: {},
  };
  const styleSubtitulo = {
    font: { name: 'Calibri', sz: 11, italic: true, color: { rgb: 'CBD5E1' } },
    fill: { fgColor: { rgb: '1C2837' } },
    alignment: { vertical: 'center', horizontal: 'center' },
  };
  const styleKpiLabel = {
    font: { name: 'Calibri', sz: 9, bold: true, color: { rgb: '94A3B8' } },
    fill: { fgColor: { rgb: 'F8FAFC' } },
    alignment: { vertical: 'center', horizontal: 'left', wrapText: true },
    border: { top: { style: 'thin', color: { rgb: 'E2E8F0' } }, bottom: { style: 'thin', color: { rgb: 'E2E8F0' } }, left: { style: 'thin', color: { rgb: 'E2E8F0' } }, right: { style: 'thin', color: { rgb: 'E2E8F0' } } },
  };
  const styleKpiVal = (color) => ({
    font: { name: 'Calibri', sz: 14, bold: true, color: { rgb: color } },
    fill: { fgColor: { rgb: 'F8FAFC' } },
    alignment: { vertical: 'center', horizontal: 'left' },
    border: { top: { style: 'thin', color: { rgb: 'E2E8F0' } }, bottom: { style: 'thin', color: { rgb: 'E2E8F0' } }, left: { style: 'thin', color: { rgb: 'E2E8F0' } }, right: { style: 'thin', color: { rgb: 'E2E8F0' } } },
    numFmt: '"$"#,##0',
  });
  const styleHeader = {
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb: '1C2837' } },
    alignment: { vertical: 'center', horizontal: 'center', wrapText: true },
    border: { top: { style: 'thin', color: { rgb: '1C2837' } }, bottom: { style: 'thin', color: { rgb: '1C2837' } }, left: { style: 'thin', color: { rgb: '334155' } }, right: { style: 'thin', color: { rgb: '334155' } } },
  };
  const styleCell = (align = 'left', extra = {}) => ({
    font: { name: 'Calibri', sz: 10, color: { rgb: '1E293B' } },
    alignment: { vertical: 'center', horizontal: align, wrapText: align === 'left' },
    border: { top: { style: 'thin', color: { rgb: 'E2E8F0' } }, bottom: { style: 'thin', color: { rgb: 'E2E8F0' } }, left: { style: 'thin', color: { rgb: 'E2E8F0' } }, right: { style: 'thin', color: { rgb: 'E2E8F0' } } },
    ...extra,
  });
  const styleMoney = (color = '1E293B') => styleCell('right', { numFmt: '"$"#,##0', font: { name: 'Calibri', sz: 10, color: { rgb: color } } });
  const stylePct = styleCell('center', { numFmt: '0.00%' });
  const styleTotal = (color = '1E293B') => ({
    font: { name: 'Calibri', sz: 11, bold: true, color: { rgb: color } },
    fill: { fgColor: { rgb: 'F1F5F9' } },
    alignment: { vertical: 'center', horizontal: 'right' },
    border: { top: { style: 'medium', color: { rgb: '1C2837' } }, bottom: { style: 'medium', color: { rgb: '1C2837' } }, left: { style: 'thin', color: { rgb: 'E2E8F0' } }, right: { style: 'thin', color: { rgb: 'E2E8F0' } } },
    numFmt: '"$"#,##0',
  });
  const styleEstadoCell = (rgb) => ({
    font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: 'FFFFFF' } },
    fill: { fgColor: { rgb } },
    alignment: { vertical: 'center', horizontal: 'center' },
    border: { top: { style: 'thin', color: { rgb: 'E2E8F0' } }, bottom: { style: 'thin', color: { rgb: 'E2E8F0' } }, left: { style: 'thin', color: { rgb: 'E2E8F0' } }, right: { style: 'thin', color: { rgb: 'E2E8F0' } } },
  });

  // ============================================================================
  // SHEET 1 — RESUMEN EJECUTIVO
  // ============================================================================
  const r1 = [
    ['VISTA FINANCIERA'],
    [`Reporte ejecutivo · ${corte.fecha_corte && corte.origen !== 'actual' ? 'Corte al ' : ''}${fechaHoy}${origenTxt} · ${proyectos.length} proyectos`],
    [],
    ['INDICADORES CLAVE'],
    [],
    ['Valor Contratado', totales.valor_final, '', 'Total Ejecutado', totales.valor_ejecutado],
    [],
    ['Cobrado al Cliente', totales.valor_pagado_cliente, '', 'Pendiente por Cobrar', totales.pendiente_cliente],
    [],
    [],
    ['DISTRIBUCIÓN POR ESTADO'],
    [],
    ['Estado', 'Proyectos', 'Valor Contratado', 'Ejecutado', 'Cobrado', 'Pendiente'],
  ];

  // Datos por estado
  const porEstado = {};
  proyectos.forEach(p => {
    const k = p.estado || 'otro';
    if (!porEstado[k]) porEstado[k] = { count: 0, vf: 0, ve: 0, vp: 0, pd: 0 };
    porEstado[k].count++;
    porEstado[k].vf += p.valor_final || 0;
    porEstado[k].ve += p.valor_ejecutado || 0;
    porEstado[k].vp += p.valor_pagado_cliente || 0;
    porEstado[k].pd += p.pendiente_cliente || 0;
  });
  Object.entries(ESTADOS).forEach(([key, cfg]) => {
    const d = porEstado[key] || { count: 0, vf: 0, ve: 0, vp: 0, pd: 0 };
    r1.push([cfg.label, d.count, d.vf, d.ve, d.vp, d.pd]);
  });
  r1.push(['TOTAL', proyectos.length, totales.valor_final, totales.valor_ejecutado, totales.valor_pagado_cliente, totales.pendiente_cliente]);

  const ws1 = XLSX.utils.aoa_to_sheet(r1);
  ws1['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } },
    { s: { r: 3, c: 0 }, e: { r: 3, c: 5 } },
    { s: { r: 10, c: 0 }, e: { r: 10, c: 5 } },
  ];
  ws1['!cols'] = [{ wch: 28 }, { wch: 15 }, { wch: 22 }, { wch: 18 }, { wch: 22 }, { wch: 22 }];
  ws1['!rows'] = [{ hpt: 36 }, { hpt: 22 }];

  // Estilos para sheet 1
  ws1['A1'].s = styleTitulo;
  ws1['A2'].s = styleSubtitulo;
  ws1['A4'].s = { ...styleTitulo, font: { ...styleTitulo.font, sz: 12 }, fill: { fgColor: { rgb: '475569' } } };
  // KPIs (filas 6, 8 — index 5, 7)
  ['A6', 'D6', 'A8', 'D8'].forEach(c => ws1[c] && (ws1[c].s = styleKpiLabel));
  ws1['B6'] && (ws1['B6'].s = styleKpiVal('3B82F6'));
  ws1['E6'] && (ws1['E6'].s = styleKpiVal('F59E0B'));
  ws1['B8'] && (ws1['B8'].s = styleKpiVal('22C55E'));
  ws1['E8'] && (ws1['E8'].s = styleKpiVal('EF4444'));
  ws1['A11'].s = { ...styleTitulo, font: { ...styleTitulo.font, sz: 12 }, fill: { fgColor: { rgb: '475569' } } };
  // Header tabla estados
  ['A13', 'B13', 'C13', 'D13', 'E13', 'F13'].forEach(c => ws1[c] && (ws1[c].s = styleHeader));
  // Filas de estados (14-19) y total (20)
  for (let i = 14; i <= 19; i++) {
    const estadoIdx = i - 14;
    const estadoKey = Object.keys(ESTADOS)[estadoIdx];
    const cfg = ESTADOS[estadoKey];
    const rgb = cfg.color.replace('#', '').toUpperCase();
    ws1[`A${i}`] && (ws1[`A${i}`].s = styleEstadoCell(rgb));
    ws1[`B${i}`] && (ws1[`B${i}`].s = styleCell('center'));
    ws1[`C${i}`] && (ws1[`C${i}`].s = styleMoney());
    ws1[`D${i}`] && (ws1[`D${i}`].s = styleMoney('F59E0B'));
    ws1[`E${i}`] && (ws1[`E${i}`].s = styleMoney('22C55E'));
    ws1[`F${i}`] && (ws1[`F${i}`].s = styleMoney('EF4444'));
  }
  ws1['A20'] && (ws1['A20'].s = { ...styleTotal('1C2837'), alignment: { vertical: 'center', horizontal: 'center' } });
  ws1['B20'] && (ws1['B20'].s = { ...styleTotal(), alignment: { vertical: 'center', horizontal: 'center' }, numFmt: '0' });
  ws1['C20'] && (ws1['C20'].s = styleTotal('3B82F6'));
  ws1['D20'] && (ws1['D20'].s = styleTotal('F59E0B'));
  ws1['E20'] && (ws1['E20'].s = styleTotal('22C55E'));
  ws1['F20'] && (ws1['F20'].s = styleTotal('EF4444'));

  XLSX.utils.book_append_sheet(wb, ws1, 'Resumen Ejecutivo');

  // ============================================================================
  // SHEET 2 — DETALLE PROYECTOS
  // ============================================================================
  // Color de relleno para el semáforo de días de ejecución (mismo criterio del sistema)
  const RGB_SEMAFORO = { verde: '16A34A', amarillo: 'D97706', rojo: 'DC2626' };

  const headers2 = ['#', 'Proyecto', 'Cliente', 'Cédula', 'Apto', 'Área m²', 'Estado', 'Ejecución desde', 'Valor Final', 'Fact. Pagadas', 'Fact. Montadas', 'Fact. Por Pagar', 'Gasto Manual', 'Caja Menor', 'Bodega', 'Total Ejecutado', '% Ejec', 'Cobrado Cliente', '% Cob', 'Pendiente', 'Utilidad Actual'];
  const r2 = [
    ['DETALLE FINANCIERO POR PROYECTO'],
    [`${corte.fecha_corte && corte.origen !== 'actual' ? 'Corte al' : 'Generado'} ${fechaHoy}${origenTxt} · ${proyectos.length} proyectos · Total Ejecutado = Fact. Pagadas + Gasto Manual + Caja Menor + Bodega · Fact. Por Pagar = Montadas - Pagadas`],
    [],
    headers2,
  ];
  proyectos.forEach((p, i) => {
    r2.push([
      i + 1,
      p.proyecto_nombre || '-',
      p.nombre_cliente || '-',
      p.cedula || '',
      p.apartamento || '',
      p.area_m2 || null,
      ESTADOS[p.estado]?.label || p.estado || '-',
      semaforoCronograma(p.fecha_inicio_cronograma)?.dias ?? '',
      p.valor_final || 0,
      p.valor_ejecutado_facturas || 0,
      p.facturas_montadas || 0,
      p.facturas_pendiente || 0,
      p.gasto_manual || 0,
      p.caja_menor || 0,
      p.salidas_bodega || 0,
      p.valor_ejecutado || 0,
      (p.porcentaje_ejecutado || 0) / 100,
      p.valor_pagado_cliente || 0,
      (p.porcentaje_cobrado || 0) / 100,
      p.pendiente_cliente || 0,
      p.utilidad_actual || 0,
    ]);
  });
  // Fila de totales
  const sumF = (k) => proyectos.reduce((s, p) => s + (p[k] || 0), 0);
  const totRow = ['', `TOTAL (${proyectos.length})`, '', '', '', '', '', '',
    sumF('valor_final'),
    sumF('valor_ejecutado_facturas'),
    sumF('facturas_montadas'),
    sumF('facturas_pendiente'),
    sumF('gasto_manual'),
    sumF('caja_menor'),
    sumF('salidas_bodega'),
    sumF('valor_ejecutado'),
    '',
    sumF('valor_pagado_cliente'),
    '',
    sumF('pendiente_cliente'),
    sumF('utilidad_actual'),
  ];
  r2.push(totRow);

  const ws2 = XLSX.utils.aoa_to_sheet(r2);
  ws2['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 20 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 20 } },
  ];
  ws2['!cols'] = [
    { wch: 5 }, { wch: 32 }, { wch: 28 }, { wch: 14 }, { wch: 12 }, { wch: 9 },
    { wch: 14 }, { wch: 15 }, { wch: 15 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 13 }, { wch: 12 }, { wch: 15 }, { wch: 9 }, { wch: 15 }, { wch: 9 }, { wch: 15 }, { wch: 15 },
  ];
  ws2['!rows'] = [{ hpt: 30 }, { hpt: 22 }, undefined, { hpt: 36 }];
  ws2['!freeze'] = { xSplit: 0, ySplit: 4 };
  ws2['!autofilter'] = { ref: `A4:U${4 + proyectos.length}` };

  ws2['A1'].s = styleTitulo;
  ws2['A2'].s = styleSubtitulo;
  // Header row (fila 4 = index 3)
  for (let c = 0; c < headers2.length; c++) {
    const cell = XLSX.utils.encode_cell({ r: 3, c });
    if (ws2[cell]) ws2[cell].s = styleHeader;
  }
  // Data rows
  for (let i = 0; i < proyectos.length; i++) {
    const r = 4 + i;
    const p = proyectos[i];
    const rgb = (ESTADOS[p.estado]?.color || '#94a3b8').replace('#', '').toUpperCase();
    ws2[XLSX.utils.encode_cell({ r, c: 0 })].s = styleCell('center');
    ws2[XLSX.utils.encode_cell({ r, c: 1 })].s = styleCell('left', { font: { name: 'Calibri', sz: 10, bold: true, color: { rgb: '1E293B' } } });
    ws2[XLSX.utils.encode_cell({ r, c: 2 })].s = styleCell('left');
    ws2[XLSX.utils.encode_cell({ r, c: 3 })].s = styleCell('center');
    ws2[XLSX.utils.encode_cell({ r, c: 4 })].s = styleCell('center');
    if (ws2[XLSX.utils.encode_cell({ r, c: 5 })]) ws2[XLSX.utils.encode_cell({ r, c: 5 })].s = styleCell('center', { numFmt: '0.00' });
    ws2[XLSX.utils.encode_cell({ r, c: 6 })].s = styleEstadoCell(rgb);
    // Ejecución desde (días) — celda coloreada con el semáforo del sistema
    const semX = semaforoCronograma(p.fecha_inicio_cronograma);
    const celdaDias = ws2[XLSX.utils.encode_cell({ r, c: 7 })];
    if (celdaDias) {
      celdaDias.s = semX
        ? styleEstadoCell(RGB_SEMAFORO[semX.nivel])
        : styleCell('center');
    }
    ws2[XLSX.utils.encode_cell({ r, c: 8 })].s = styleMoney('3B82F6');       // Valor Final
    ws2[XLSX.utils.encode_cell({ r, c: 9 })].s = styleMoney('64748B');       // Fact. Pagadas
    ws2[XLSX.utils.encode_cell({ r, c: 10 })].s = styleMoney('64748B');      // Fact. Montadas
    ws2[XLSX.utils.encode_cell({ r, c: 11 })].s = styleMoney((p.facturas_pendiente || 0) > 0.5 ? 'DC2626' : '64748B'); // Fact. Por Pagar
    ws2[XLSX.utils.encode_cell({ r, c: 12 })].s = styleMoney('64748B');      // Gasto Manual
    ws2[XLSX.utils.encode_cell({ r, c: 13 })].s = styleMoney('64748B');      // Caja Menor
    ws2[XLSX.utils.encode_cell({ r, c: 14 })].s = styleMoney('64748B');      // Bodega
    ws2[XLSX.utils.encode_cell({ r, c: 15 })].s = styleMoney('F59E0B');      // Total Ejecutado
    ws2[XLSX.utils.encode_cell({ r, c: 16 })].s = stylePct;                  // % Ejec
    ws2[XLSX.utils.encode_cell({ r, c: 17 })].s = styleMoney('22C55E');      // Cobrado Cliente
    ws2[XLSX.utils.encode_cell({ r, c: 18 })].s = stylePct;                  // % Cob
    ws2[XLSX.utils.encode_cell({ r, c: 19 })].s = styleMoney('EF4444');      // Pendiente
    ws2[XLSX.utils.encode_cell({ r, c: 20 })].s = styleMoney((p.utilidad_actual || 0) >= 0 ? '059669' : 'DC2626'); // Utilidad
  }
  // Totales (última fila)
  const lastR = 4 + proyectos.length;
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 1 })].s = { ...styleTotal(), alignment: { vertical: 'center', horizontal: 'left' } };
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 8 })].s = styleTotal('3B82F6');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 9 })].s = styleTotal('64748B');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 10 })].s = styleTotal('64748B');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 11 })].s = styleTotal('DC2626');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 12 })].s = styleTotal('64748B');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 13 })].s = styleTotal('64748B');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 14 })].s = styleTotal('64748B');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 15 })].s = styleTotal('F59E0B');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 17 })].s = styleTotal('22C55E');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 19 })].s = styleTotal('EF4444');
  ws2[XLSX.utils.encode_cell({ r: lastR, c: 20 })].s = styleTotal('059669');

  XLSX.utils.book_append_sheet(wb, ws2, 'Detalle Proyectos');

  // ============================================================================
  // SHEET 3 — DATOS DE CONTACTO
  // ============================================================================
  const headers3 = ['#', 'Proyecto', 'Apto', 'Cliente', 'Cédula', 'Teléfono', 'Correo', 'Fecha Cotización', 'Fecha Entrega Estimada', 'Estado'];
  const r3 = [
    ['DATOS DE CONTACTO'],
    [`Información de clientes · ${proyectos.length} proyectos`],
    [],
    headers3,
  ];
  proyectos.forEach((p, i) => {
    r3.push([
      i + 1,
      p.proyecto_nombre || '-',
      p.apartamento || '',
      p.nombre_cliente || '-',
      p.cedula || '',
      p.telefono || '',
      p.correo || '',
      p.fecha_cotizacion || '',
      p.fecha_entrega_estimada || '',
      ESTADOS[p.estado]?.label || p.estado || '-',
    ]);
  });
  const ws3 = XLSX.utils.aoa_to_sheet(r3);
  ws3['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: 9 } },
  ];
  ws3['!cols'] = [{ wch: 5 }, { wch: 30 }, { wch: 10 }, { wch: 30 }, { wch: 14 }, { wch: 14 }, { wch: 30 }, { wch: 16 }, { wch: 18 }, { wch: 14 }];
  ws3['!rows'] = [{ hpt: 30 }, { hpt: 20 }, undefined, { hpt: 28 }];
  ws3['!autofilter'] = { ref: `A4:J${4 + proyectos.length}` };
  ws3['A1'].s = styleTitulo;
  ws3['A2'].s = styleSubtitulo;
  for (let c = 0; c < headers3.length; c++) {
    const cell = XLSX.utils.encode_cell({ r: 3, c });
    if (ws3[cell]) ws3[cell].s = styleHeader;
  }
  for (let i = 0; i < proyectos.length; i++) {
    const r = 4 + i;
    const p = proyectos[i];
    const rgb = (ESTADOS[p.estado]?.color || '#94a3b8').replace('#', '').toUpperCase();
    for (let c = 0; c <= 8; c++) {
      const cell = XLSX.utils.encode_cell({ r, c });
      if (ws3[cell]) ws3[cell].s = styleCell(c === 0 || c === 2 ? 'center' : 'left');
    }
    if (ws3[XLSX.utils.encode_cell({ r, c: 9 })]) ws3[XLSX.utils.encode_cell({ r, c: 9 })].s = styleEstadoCell(rgb);
  }
  XLSX.utils.book_append_sheet(wb, ws3, 'Contacto Clientes');

  // Descargar
  const filename = `Vista_Financiera_${corte.fecha_corte || new Date().toISOString().slice(0, 10)}.xlsx`;
  XLSX.writeFile(wb, filename);
}

export default function VistaFinancieraGerencia() {
  const { error, success } = useNotification();
  const { tienePermiso } = useAuth();
  const [loading, setLoading] = useState(true);
  const [proyectos, setProyectos] = useState([]);
  const [totales, setTotales] = useState(null);
  const [busqueda, setBusqueda] = useState('');
  // Multi-selección de estados: array vacío = "Todos". Cada chip togglea su
  // estado; KPIs y tabla se calculan sobre la combinación seleccionada.
  const [filtroEstados, setFiltroEstados] = useState([]);
  // Orden por días de ejecución: null (sin orden) | 'desc' (mayor→menor) | 'asc'
  const [sortDias, setSortDias] = useState(null);
  const [descargando, setDescargando] = useState(false);
  // Fecha de corte: hoy = valores actuales; una fecha anterior = foto de ese día o reconstrucción.
  const [fechaCorte, setFechaCorte] = useState(hoyBogota());
  const [corte, setCorte] = useState({ origen: 'actual', fecha_corte: hoyBogota(), caja_bancos: null, nota: null });
  const [fotos, setFotos] = useState([]);
  const esActual = corte.origen === 'actual';

  // Caja (Bancos): valor global editable solo con permiso gerencia.vista-financiera-caja
  const puedeEditarCaja = tienePermiso('gerencia', 'vista-financiera-caja');
  const [caja, setCaja] = useState(0);
  const [modalCaja, setModalCaja] = useState({ visible: false, valor: '', guardando: false });

  const cargarCaja = useCallback(async () => {
    try {
      const d = await client.query(`{ cajaBancos }`);
      setCaja(Number(d.cajaBancos || 0));
    } catch { /* silencioso */ }
  }, []);
  useEffect(() => { cargarCaja(); }, [cargarCaja]);

  const guardarCaja = async () => {
    const v = parseFloat(String(modalCaja.valor).replace(/[^\d.-]/g, '')) || 0;
    setModalCaja(prev => ({ ...prev, guardando: true }));
    try {
      const r = await client.mutate(`mutation($v: Float!) { actualizarCajaBancos(valor: $v) { success message } }`, { v });
      if (r.actualizarCajaBancos.success) {
        setCaja(v);
        setModalCaja({ visible: false, valor: '', guardando: false });
        success('Caja actualizada');
      } else {
        error(r.actualizarCajaBancos.message || 'Error al guardar');
        setModalCaja(prev => ({ ...prev, guardando: false }));
      }
    } catch (e) {
      error('Error de conexión al guardar la caja');
      setModalCaja(prev => ({ ...prev, guardando: false }));
    }
  };

  const toggleEstado = (key) => {
    setFiltroEstados(prev => prev.includes(key)
      ? prev.filter(k => k !== key)
      : [...prev, key]);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const hoy = hoyBogota();
      const fc = fechaCorte && fechaCorte < hoy ? fechaCorte : null;
      const data = await client.query(QUERY, { fc });
      const r = data.proyectosFinancieros;
      setProyectos(r.proyectos || []);
      setTotales(r.totales || null);
      setCorte({ origen: r.origen || 'actual', fecha_corte: r.fecha_corte || hoy, caja_bancos: r.caja_bancos ?? null, nota: r.nota || null });
    } catch (e) { error(e?.message?.includes('fecha') ? e.message : 'Error cargando Vista Financiera'); }
    finally { setLoading(false); }
  }, [error, fechaCorte]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Fechas con foto guardada (para mostrar desde cuándo hay historial exacto)
  useEffect(() => {
    client.query(`{ fotosFinancieras }`).then(d => setFotos(d.fotosFinancieras || [])).catch(() => {});
  }, []);

  const filtrados = useMemo(() => {
    let lista = proyectos;
    if (filtroEstados.length > 0) lista = lista.filter(p => filtroEstados.includes(p.estado));
    const q = busqueda.trim().toLowerCase();
    if (q) lista = lista.filter(p =>
      (p.proyecto_nombre || '').toLowerCase().includes(q) ||
      (p.nombre_cliente || '').toLowerCase().includes(q) ||
      (p.apartamento || '').toLowerCase().includes(q)
    );
    // Orden opcional por días de ejecución (los que no tienen cronograma van al final)
    if (sortDias) {
      lista = [...lista].sort((a, b) => {
        const da = semaforoCronograma(a.fecha_inicio_cronograma)?.dias;
        const db = semaforoCronograma(b.fecha_inicio_cronograma)?.dias;
        if (da == null && db == null) return 0;
        if (da == null) return 1;
        if (db == null) return -1;
        return sortDias === 'asc' ? da - db : db - da;
      });
    }
    return lista;
  }, [proyectos, busqueda, filtroEstados, sortDias]);

  const totalesFiltrados = useMemo(() => ({
    valor_final: filtrados.reduce((s, p) => s + (p.valor_final || 0), 0),
    valor_ejecutado: filtrados.reduce((s, p) => s + (p.valor_ejecutado || 0), 0),
    valor_pagado_cliente: filtrados.reduce((s, p) => s + (p.valor_pagado_cliente || 0), 0),
    pendiente_cliente: filtrados.reduce((s, p) => s + (p.pendiente_cliente || 0), 0),
  }), [filtrados]);

  const conteoEstado = useMemo(() => {
    const c = { todos: proyectos.length };
    proyectos.forEach(p => { c[p.estado] = (c[p.estado] || 0) + 1; });
    return c;
  }, [proyectos]);

  // Indicadores de ejecución y cobro. El 80% del contratado es el presupuesto
  // de costo (techo de gasto); el 100% es lo que se debe cobrar al cliente.
  // Posición proyectada = Caja + Falta por cobrar − Falta por ejecutar.
  const rent = useMemo(() => {
    const contratado = totalesFiltrados.valor_final;
    const ejecutado = totalesFiltrados.valor_ejecutado;
    const cobrado = totalesFiltrados.valor_pagado_cliente;
    // Ejecución
    const deberiaEjecutar = contratado * 0.80;
    const faltaEjecutar = deberiaEjecutar - ejecutado;   // (−) si ya excedió el techo
    const pctEjecutado = deberiaEjecutar > 0 ? (ejecutado / deberiaEjecutar) * 100 : 0;
    // Cobro
    const deberiaCobrar = contratado;
    const faltaCobrar = deberiaCobrar - cobrado;
    const pctCobrado = deberiaCobrar > 0 ? (cobrado / deberiaCobrar) * 100 : 0;
    // Posición proyectada con la caja (en una fecha anterior, la caja de la foto; si no se conoce, null)
    const cajaUsada = esActual ? caja : corte.caja_bancos;
    const posicion = cajaUsada == null ? null : cajaUsada + faltaCobrar - faltaEjecutar;
    // Semáforo de ejecución: verde dentro del 80%, naranja 80-100%, rojo > contrato
    let nivel = 'ok';
    if (ejecutado > contratado) nivel = 'perdida';
    else if (ejecutado > deberiaEjecutar) nivel = 'sobrecosto';
    return { contratado, ejecutado, deberiaEjecutar, faltaEjecutar, pctEjecutado,
             cobrado, deberiaCobrar, faltaCobrar, pctCobrado, posicion, nivel, cajaUsada };
  }, [totalesFiltrados, caja, esActual, corte.caja_bancos]);

  const handleDescargar = async () => {
    if (filtrados.length === 0) { error('No hay proyectos para exportar'); return; }
    setDescargando(true);
    try {
      generarExcel(filtrados, totalesFiltrados, corte);
      success('Excel descargado');
      // Al descargar con valores actuales se guarda la foto de hoy: así ese informe
      // se puede volver a consultar tal cual en el futuro.
      if (esActual) {
        try {
          const r = await client.mutate(`mutation { guardarFotoFinanciera { success message } }`);
          if (r.guardarFotoFinanciera?.success) setFotos(prev => (prev.includes(corte.fecha_corte) ? prev : [corte.fecha_corte, ...prev]));
        } catch { /* la descarga ya salió; la foto es un extra */ }
      }
    } catch (e) {
      console.error(e);
      error('Error generando el Excel');
    } finally {
      setDescargando(false);
    }
  };

  if (loading) return (
    <div className="vfg-module"><div className="vfg-loading">
      <div className="vfg-spinner" /><p>Cargando...</p>
    </div></div>
  );

  return (
    <div className="vfg-module">
      <div className="vfg-header">
        <div>
          <h1>Vista Financiera</h1>
          <p>Resumen completo de proyectos: contratado, ejecutado y cobrado</p>
        </div>
        <div className="vfg-header-actions">
          <div className="vfg-corte">
            <label htmlFor="vfg-fecha-corte">Fecha de corte</label>
            <input id="vfg-fecha-corte" type="date" value={fechaCorte} max={hoyBogota()}
              onChange={e => setFechaCorte(e.target.value || hoyBogota())} />
            {!esActual && <button type="button" className="vfg-corte-hoy" onClick={() => setFechaCorte(hoyBogota())}>Hoy</button>}
          </div>
          <button
            className="vfg-btn-excel"
            onClick={handleDescargar}
            disabled={descargando || filtrados.length === 0}
            title={esActual ? 'Descargar Excel detallado para gerencia (guarda la foto de hoy)' : `Descargar el informe al ${corte.fecha_corte}`}
          >
            {descargando ? '⏳ Generando...' : '📊 Descargar Excel'}
          </button>
        </div>
      </div>

      {!esActual && (
        <div className={`vfg-aviso-corte ${corte.origen}`}>
          <strong>Informe al {fechaLargaCO(corte.fecha_corte)} · {ORIGEN_LABEL[corte.origen] || corte.origen}.</strong>
          {corte.nota ? ` ${corte.nota}` : ''}
          {fotos.length > 0 && corte.origen === 'reconstruido' ? ` Hay fotos exactas desde el ${fechaLargaCO(fotos[fotos.length - 1])}.` : ''}
        </div>
      )}
      {esActual && fotos.length > 0 && (
        <div className="vfg-fotos-info">Fotos diarias guardadas desde el {fechaLargaCO(fotos[fotos.length - 1])} ({fotos.length} días): elige una fecha anterior para ver el informe tal como estaba ese día.</div>
      )}

      {/* KPIs */}
      <div className="vfg-kpis">
        <div className="vfg-kpi"><span className="vfg-kpi-label">Valor Contratado</span><span className="vfg-kpi-val blue">{formatCOP(totalesFiltrados.valor_final)}</span></div>
        <div className="vfg-kpi"><span className="vfg-kpi-label">Total Ejecutado</span><span className="vfg-kpi-val orange">{formatCOP(totalesFiltrados.valor_ejecutado)}</span></div>
        <div className="vfg-kpi"><span className="vfg-kpi-label">Cobrado al Cliente</span><span className="vfg-kpi-val green">{formatCOP(totalesFiltrados.valor_pagado_cliente)}</span></div>
        <div className="vfg-kpi"><span className="vfg-kpi-label">Pendiente por Cobrar</span><span className="vfg-kpi-val red">{formatCOP(totalesFiltrados.pendiente_cliente)}</span></div>
      </div>

      {/* Indicadores de Ejecución y Cobro */}
      <div className="vfg-ind-row">
        {/* Bloque EJECUCIÓN */}
        <div className={`vfg-ind-block vfg-ind-${rent.nivel}`}>
          <div className="vfg-ind-head">
            <span className="vfg-ind-title">🔨 Ejecución (vs 80% del contrato)</span>
            <span className={`vfg-ind-badge vfg-ind-badge-${rent.nivel}`}>
              {rent.nivel === 'ok' && '🟢 Dentro de presupuesto'}
              {rent.nivel === 'sobrecosto' && '🟡 Sobrecosto'}
              {rent.nivel === 'perdida' && '🔴 Pérdida'}
            </span>
          </div>
          <div className="vfg-ind-cells">
            <div className="vfg-ind-cell"><span className="vfg-ind-lbl">Ejecutado</span><span className="vfg-ind-val orange">{formatCOP(rent.ejecutado)}</span></div>
            <div className="vfg-ind-cell"><span className="vfg-ind-lbl">Debería ejecutar (80%)</span><span className="vfg-ind-val">{formatCOP(rent.deberiaEjecutar)}</span></div>
            <div className="vfg-ind-cell"><span className="vfg-ind-lbl">Falta por ejecutar</span><span className={`vfg-ind-val ${rent.faltaEjecutar < 0 ? 'neg' : 'pos'}`}>{formatCOP(rent.faltaEjecutar)}</span></div>
          </div>
          <div className="vfg-ind-bar">
            <div className={`vfg-ind-fill vfg-ind-fill-${rent.nivel}`} style={{ width: `${Math.min(rent.pctEjecutado, 100)}%` }} />
          </div>
          <span className="vfg-ind-pct">{rent.pctEjecutado.toFixed(1)}% del presupuesto de ejecución</span>
        </div>

        {/* Bloque COBRO */}
        <div className="vfg-ind-block">
          <div className="vfg-ind-head">
            <span className="vfg-ind-title">💰 Cobro al cliente (vs 100% del contrato)</span>
          </div>
          <div className="vfg-ind-cells">
            <div className="vfg-ind-cell"><span className="vfg-ind-lbl">Cobrado</span><span className="vfg-ind-val green">{formatCOP(rent.cobrado)}</span></div>
            <div className="vfg-ind-cell"><span className="vfg-ind-lbl">Debería cobrar (100%)</span><span className="vfg-ind-val">{formatCOP(rent.deberiaCobrar)}</span></div>
            <div className="vfg-ind-cell"><span className="vfg-ind-lbl">Falta por cobrar</span><span className="vfg-ind-val neg">{formatCOP(rent.faltaCobrar)}</span></div>
          </div>
          <div className="vfg-ind-bar">
            <div className="vfg-ind-fill vfg-ind-fill-cobro" style={{ width: `${Math.min(rent.pctCobrado, 100)}%` }} />
          </div>
          <span className="vfg-ind-pct">{rent.pctCobrado.toFixed(1)}% cobrado del contrato</span>
        </div>
      </div>

      {/* Caja (Bancos) + Posición proyectada — solo con permiso */}
      {puedeEditarCaja && (
        <div className="vfg-caja-row">
          <div className="vfg-caja-cell">
            <span className="vfg-ind-lbl">🏦 Caja (Bancos){!esActual ? ` al ${corte.fecha_corte}` : ''}</span>
            <span className="vfg-caja-val">{rent.cajaUsada == null ? 'No disponible para esa fecha' : formatCOP(rent.cajaUsada)}</span>
            {esActual && <button className="vfg-caja-edit" onClick={() => setModalCaja({ visible: true, valor: String(caja), guardando: false })}>✎ Editar</button>}
          </div>
          <div className="vfg-caja-cell vfg-caja-pos">
            <span className="vfg-ind-lbl">Posición Proyectada</span>
            <span className={`vfg-caja-posval ${rent.posicion == null ? '' : rent.posicion >= 0 ? 'pos' : 'neg'}`}>{rent.posicion == null ? '—' : formatCOP(rent.posicion)}</span>
            <span className="vfg-caja-sub">{rent.posicion == null ? 'Sin caja de ese día no se puede calcular' : 'Caja + Falta por cobrar − Falta por ejecutar'}</span>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="vfg-toolbar">
        <div className="vfg-search-wrap">
          <span className="vfg-search-icon">🔍</span>
          <input className="vfg-search" type="text" placeholder="Buscar proyecto, cliente o apartamento..."
            value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        </div>
        <span className="vfg-count">{filtrados.length} de {proyectos.length} proyectos</span>
      </div>

      {/* Filtros estado (multi-selección: cada chip togglea, los KPIs y la tabla
          suman la combinación seleccionada) */}
      <div className="vfg-filtros">
        <button className={`vfg-filtro${filtroEstados.length === 0 ? ' active' : ''}`} onClick={() => setFiltroEstados([])}>
          Todos <span className="vfg-filtro-count">{conteoEstado.todos}</span>
        </button>
        {Object.entries(ESTADOS).map(([key, cfg]) => {
          const activo = filtroEstados.includes(key);
          return (
            <button key={key}
              className={`vfg-filtro${activo ? ' active' : ''}`}
              style={activo ? { borderColor: cfg.color, background: cfg.color + '18', color: cfg.color } : {}}
              onClick={() => toggleEstado(key)}>
              <span className="vfg-filtro-dot" style={{ background: cfg.color }} />
              {cfg.label}
              {conteoEstado[key] ? <span className="vfg-filtro-count">{conteoEstado[key]}</span> : null}
            </button>
          );
        })}
      </div>

      {/* Tabla */}
      <div className="vfg-table-wrap">
        <table className="vfg-table">
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>Proyecto</th>
              <th>Cliente</th>
              <th style={{ width: 90 }}>Apto</th>
              <th style={{ width: 100 }}>Estado</th>
              <th
                className="c vfg-th-sort"
                style={{ width: 130, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => setSortDias(s => (s === 'desc' ? 'asc' : s === 'asc' ? null : 'desc'))}
                title="Ordenar por días de ejecución (mayor ↔ menor)"
              >
                Ejecución desde{' '}
                <span style={{ opacity: 0.7 }}>{sortDias === 'asc' ? '▲' : sortDias === 'desc' ? '▼' : '⇅'}</span>
              </th>
              <th className="r" style={{ width: 130 }}>Valor Final</th>
              <th className="r" style={{ width: 150 }}>Valor Ejecutado</th>
              <th className="r" style={{ width: 150 }}>Cobrado Cliente</th>
              <th className="r" style={{ width: 130 }}>Pendiente</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.length === 0 ? (
              <tr><td colSpan={10} className="vfg-empty">No se encontraron proyectos</td></tr>
            ) : (
              <>
                {filtrados.map((p, i) => (
                  <tr key={p.id}>
                    <td className="c vfg-num">{i + 1}</td>
                    <td><div className="vfg-pname" title={p.proyecto_nombre}>{p.proyecto_nombre || '-'}</div></td>
                    <td>
                      <div className="vfg-cname">{p.nombre_cliente || '-'}</div>
                      {p.cedula && <div className="vfg-ced">CC {p.cedula}</div>}
                    </td>
                    <td>{p.apartamento || '-'}</td>
                    <td><Badge estado={p.estado} /></td>
                    <td className="c">
                      {(() => {
                        const sem = semaforoCronograma(p.fecha_inicio_cronograma);
                        if (!sem) return <span className="vfg-sem-vacio">—</span>;
                        return (
                          <span
                            className="vfg-semaforo"
                            style={{ color: sem.color, background: sem.bg, borderColor: sem.border }}
                            title={`${sem.dias} días desde el inicio del cronograma · ${sem.label} (45 = amarillo, 60 = rojo)`}
                          >
                            ● {sem.dias}d
                          </span>
                        );
                      })()}
                    </td>
                    <td className="r vfg-monto">{formatCOP(p.valor_final)}</td>
                    <td className="r"><BarVal valor={p.valor_ejecutado} pct={p.porcentaje_ejecutado} /></td>
                    <td className="r"><BarVal valor={p.valor_pagado_cliente} pct={p.porcentaje_cobrado} /></td>
                    <td className={`r ${(p.pendiente_cliente || 0) > 0 ? 'vfg-pend-pos' : 'vfg-pend-zero'}`}>
                      {formatCOP(p.pendiente_cliente)}
                    </td>
                  </tr>
                ))}
                <tr className="vfg-totals">
                  <td className="c" /><td colSpan={4}><strong>TOTALES ({filtrados.length})</strong></td>
                  <td className="r"><strong>{formatCOP(totalesFiltrados.valor_final)}</strong></td>
                  <td className="r"><strong>{formatCOP(totalesFiltrados.valor_ejecutado)}</strong></td>
                  <td className="r"><strong>{formatCOP(totalesFiltrados.valor_pagado_cliente)}</strong></td>
                  <td className="r"><strong>{formatCOP(totalesFiltrados.pendiente_cliente)}</strong></td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal editar Caja (Bancos) */}
      {modalCaja.visible && (
        <div className="vfg-modal-overlay" onClick={() => setModalCaja({ visible: false, valor: '', guardando: false })}>
          <div className="vfg-modal" onClick={e => e.stopPropagation()}>
            <h3>🏦 Editar Caja (Bancos)</h3>
            <p className="vfg-modal-sub">Valor global de la empresa. Se usa para la posición proyectada.</p>
            <label className="vfg-modal-label">Valor en caja</label>
            <input className="vfg-modal-input" type="number" value={modalCaja.valor} autoFocus
              onChange={e => setModalCaja(prev => ({ ...prev, valor: e.target.value }))}
              placeholder="0" step="1" />
            <div className="vfg-modal-actions">
              <button className="vfg-btn-sec" onClick={() => setModalCaja({ visible: false, valor: '', guardando: false })} disabled={modalCaja.guardando}>Cancelar</button>
              <button className="vfg-btn-pri" onClick={guardarCaja} disabled={modalCaja.guardando}>{modalCaja.guardando ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
